/**
 * Virtual-clock policy shared by the guest timers and the engine
 * (ADR-0007, docs/sandbox.md §3.5). Pure numbers in, numbers out.
 *
 * The guest bundles this module, so it sticks to operators: no `Math` or
 * other globals the guest could have replaced by the time it runs.
 */

/** Every clock read by guest code advances virtual time by this much. */
export const CLOCK_QUANTUM_MS = 0.05;
/**
 * This many guest clock reads within one task (no timer fired in between)
 * is a busy-wait timing loop, a classic sandbox/debugger check.
 */
export const TIMING_LOOP_READS = 100;
/** Timers scheduled at least this far out are tagged `delayed:<duration>`. */
export const LONG_DELAY_MS = 60_000;
/** `requestAnimationFrame` ticks at a virtual 60 Hz. */
export const FRAME_MS = 1000 / 60;
/** Browsers treat larger delays as 0 (they overflow a signed 32-bit int). */
export const MAX_TIMER_DELAY = 2 ** 31 - 1;
/** From this nesting level on, browsers clamp timer delays to `NESTED_MIN_DELAY`. */
export const NESTING_CLAMP_LEVEL = 5;
export const NESTED_MIN_DELAY = 4;

const truncate = (n: number): number => n - (n % 1);

/**
 * A timer's effective delay, as the HTML timer steps compute it: `delay` is
 * already a number (the guest converted it); `nesting` is the nesting level
 * of the task scheduling it.
 */
export function normalizeDelay(delay: number, nesting: number): number {
  // NaN fails both comparisons.
  let ms = delay >= 0 && delay <= MAX_TIMER_DELAY ? truncate(delay) : 0;
  if (nesting >= NESTING_CLAMP_LEVEL && ms < NESTED_MIN_DELAY) ms = NESTED_MIN_DELAY;
  return ms;
}

const UNITS: [number, string][] = [
  [864e5, "d"],
  [36e5, "h"],
  [60e3, "m"],
  [1e3, "s"],
];

/** A compact duration with at most two units: `14d`, `1h30m`, `250ms`. */
export function formatDelay(ms: number): string {
  for (let i = 0; i < UNITS.length; i++) {
    const [size, unit] = UNITS[i] as [number, string];
    if (ms < size) continue;
    let out = `${truncate(ms / size)}${unit}`;
    const next = UNITS[i + 1];
    if (next) {
      const rest = truncate((ms % size) / next[0]);
      if (rest > 0) out += `${rest}${next[1]}`;
    }
    return out;
  }
  return `${truncate(ms)}ms`;
}

/** The `delayed:<duration>` tag for a long timer delay, or null. */
export function delayTag(delay: number): string | null {
  return delay >= LONG_DELAY_MS ? `delayed:${formatDelay(delay)}` : null;
}

/** When the next animation frame after `now` runs. */
export function nextFrameTime(now: number): number {
  return (truncate(now / FRAME_MS) + 1) * FRAME_MS;
}
